import { addServerRequest, generateRequestId, hashRequestBody, type RequestLog } from '@/utils/requestTracker';
import { buildUrl } from './buildUrl';
import { Deployment } from '@/types/deployment';
import { ErrorResponse } from '@/types/apiTypes';
import { isErrorResponse } from '@/data/utils';
import { isServer } from '@tanstack/react-query';
import { logger } from '@/utils/logger';
import { Method } from '../enums/method';
import { Services } from '../enums/services';

// only logs if DEBUG_NETWORK is set
// example usage: $ DEBUG_NETWORK_REQUESTS=true pnpm local
export const log = process.env.DEBUG_NETWORK_REQUESTS
    ? logger('helpers/makeFetch')
    : {
          debug: () => {},
          error: () => {},
          info: () => {},
          warn: () => {},
      };

export type ResponseType = 'json' | 'blob' | 'full' | 'void';

export type Props<TBody, TParams> = {
    apiPath: string;
    apiService: Services;
    body?: TBody;
    deployment: Deployment;
    forceCredentials?: boolean;
    headers?: {
        [key: string]: string;
    };
    isVerboseError?: boolean;
    method: Method;
    params?: TParams;
    responseType?: ResponseType;
    signal?: AbortSignal;
    token: string;
};

export type FetchOptions = {
    body?: any;
    credentials?: RequestCredentials;
    headers: {
        [key: string]: string;
    };
    method: Method;
    signal?: AbortSignal;
};

/**
 * Builds HTTP headers for API requests
 * @param method - HTTP method to be used
 * @param token - Authentication token
 * @param customHeaders - Optional additional headers
 * @param body - Optional request body
 * @param responseType - Optional response type ('json' or 'blob')
 * @returns Record of HTTP headers
 */
export const buildHeaders = (
    method: Method,
    token: string,
    customHeaders: Record<string, string> = {},
    body?: any,
    responseType: ResponseType = 'json'
): Record<string, string> => {
    const defaultHeaders: Record<string, string> = {
        Accept: responseType === 'json' ? 'application/json' : '*/*',
        ...(method !== Method.Head && !(body instanceof FormData) && { 'Content-Type': 'application/json' }),
    };

    return {
        ...defaultHeaders,
        Authorization: `Bearer ${token}`,
        ...customHeaders,
    };
};

/**
 * Processes the request body based on HTTP method and content type
 * @param body - Request body to process
 * @param method - HTTP method being used
 * @returns Processed body or undefined if method doesn't support body
 */
export const processBody = (body: any, method: Method): any => {
    if (!isMethodWithBody(method)) {
        return undefined;
    }

    if (typeof body === 'string' || body instanceof FormData) {
        return body;
    }
    return JSON.stringify(body);
};

/**
 * Checks if the HTTP method typically includes a request body
 * @param method - HTTP method to check
 * @returns boolean indicating if method supports body
 */
const isMethodWithBody = (method: Method): boolean => {
    return [Method.Post, Method.Put, Method.Patch, Method.Delete].includes(method);
};

/**
 * Handles the API response based on method and extracts data
 * @param response - Fetch Response object
 * @param method - HTTP method used in request
 * @param responseType - Optional response type ('json' or 'blob')
 * @returns Processed response data
 */
const handleResponse = async <TResponse>(
    response: Response,
    method: Method,
    responseType: ResponseType = 'json'
): Promise<TResponse | ErrorResponse | Blob | Response | void> => {
    if (method === Method.Head) {
        return response;
    }
    if (responseType === 'full') {
        return response;
    }

    if (responseType === 'blob') {
        const blob = await response.blob();

        if (response.headers.get('content-type')?.includes('application/json')) {
            const text = await blob.text();
            let jsonData;
            try {
                jsonData = JSON.parse(text);
            } catch {
                return blob;
            }
            const result = processResponseData<unknown>(jsonData);
            if (isErrorResponse(result)) {
                throw new Error(result.message ?? result.error, { cause: result });
            }
            return blob;
        }

        return blob;
    }

    if (responseType === 'void') {
        if (response.ok) {
            return;
        }
        throw new Error(`${response.statusText}`);
    }

    if (response.ok === false) {
        const text = await response.text();
        const trimmed = text.trim();

        if (!trimmed) {
            return synthesizeHttpErrorResponse(response);
        }

        let parsed: unknown;
        try {
            parsed = JSON.parse(trimmed);
        } catch {
            return synthesizeHttpErrorResponse(response);
        }

        if (typeof parsed !== 'object' || parsed === null) {
            return synthesizeHttpErrorResponse(response);
        }

        if (isErrorResponse(parsed)) {
            return normalizeToErrorResponse(parsed);
        }

        return synthesizeHttpErrorResponse(response);
    }

    const data = await response.json();
    return processResponseData<TResponse>(data);
};

const normalizeToErrorResponse = (partial: Partial<ErrorResponse> & { error: string }): ErrorResponse => ({
    error: partial.error,
    message: partial.message,
    status: partial.status ?? 500,
    timestamp: partial.timestamp ?? new Date().toISOString(),
    validationErrors: partial.validationErrors ?? [],
    validationErrorSummary: partial.validationErrorSummary ?? [],
});

const synthesizeHttpErrorResponse = (response: Response): ErrorResponse =>
    normalizeToErrorResponse({
        error: response.statusText || 'Error',
        status: response.status,
    });

/**
 * Processes API response data — returns structured ErrorResponse for error shapes
 * instead of throwing (JSON path only; blob/void still throw).
 */
export const processResponseData = <TResponse>(data: unknown): TResponse | ErrorResponse => {
    if (isErrorResponse(data)) {
        return data;
    }

    const record = data as Record<string, unknown> | undefined;
    const payload = record?.payload;

    if (payload && typeof payload === 'object' && 'error' in payload) {
        const payloadError = (payload as Record<string, Record<string, unknown>>).error;
        return normalizeToErrorResponse({
            error: (payloadError?.message as string) ?? 'Unknown payload error',
            message: `${payloadError?.message} (${payloadError?.code})`,
            status: (payloadError?.code as number) ?? 500,
        });
    }

    // Wire boundary: JSON.parse returns unknown; this is the single trusted assertion point
    return (payload ?? data) as TResponse;
};

/**
 * Logs request details for non-production environments
 * @param url - Request URL
 * @param options - Fetch options used
 * @param deployment - Current deployment environment
 * @param data - Optional response data to log
 */
export const logRequestDetails = (url: string, options: FetchOptions, deployment: Deployment, data?: any) => {
    if (deployment === Deployment.Prod) {
        return;
    }

    log.info({ options }, `fetching ${url}`);
    if (data) {
        log.info(`response: ${JSON.stringify(data)}`);
    }
};

// Function declaration required for TypeScript overloads (exception to arrow-function preference)
export async function makeFetch<TBody = unknown, TParams = unknown>(
    props: Props<TBody, TParams> & { responseType: 'blob' }
): Promise<Blob>;
export async function makeFetch<TBody = unknown, TParams = unknown>(
    props: Props<TBody, TParams> & { responseType: 'full' }
): Promise<Response>;
export async function makeFetch<TBody = unknown, TParams = unknown>(
    props: Props<TBody, TParams> & { responseType: 'void' }
): Promise<void>;
export async function makeFetch<TResponse, TBody = unknown, TParams = unknown>(
    props: Props<TBody, TParams>
): Promise<TResponse | ErrorResponse>;
export async function makeFetch<TResponse, TBody = unknown, TParams = unknown>({
    apiPath,
    apiService,
    body,
    deployment,
    headers: customHeaders,
    isVerboseError = false,
    method,
    params,
    responseType = 'json',
    signal,
    token,
}: Props<TBody, TParams>): Promise<TResponse | ErrorResponse | Blob | Response | void> {
    const url = buildUrl({
        apiPath,
        apiService,
        deployment,
        params: params ?? {},
    });

    const options: FetchOptions = {
        credentials: 'same-origin',
        headers: buildHeaders(method, token, customHeaders, body, responseType),
        method,
    };
    if (signal) {
        options.signal = signal;
    }

    const processedBody = processBody(body, method);
    if (processedBody) {
        options.body = processedBody;
    }

    logRequestDetails(url, options, deployment);

    const requestId = generateRequestId();
    const startTime = performance.now();
    const side = isServer ? 'server' : 'client';

    const bodyHash =
        (method === Method.Post || method === Method.Put || method === Method.Patch) && body
            ? hashRequestBody(body)
            : undefined;

    const requestLog: RequestLog = {
        bodyHash,
        duration: undefined,
        endTime: undefined,
        error: undefined,
        id: requestId,
        method: method.toString(),
        side,
        startTime,
        status: undefined,
        url,
    };

    try {
        const response = await fetch(url, options);
        const endTime = performance.now();
        const duration = endTime - startTime;

        requestLog.endTime = endTime;
        requestLog.duration = duration;
        requestLog.status = response.status;
        requestLog.requestId = response.headers.get('x-wvb-request-id') ?? undefined;

        if (side === 'server') {
            addServerRequest(requestLog);
        } else {
            window.dispatchEvent(
                new CustomEvent('api-request-completed', {
                    detail: requestLog,
                })
            );
        }

        const data = await handleResponse<TResponse>(response, method, responseType);
        logRequestDetails(url, options, deployment, data instanceof Blob ? 'Blob data' : data);
        return data;
    } catch (error: unknown) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        requestLog.endTime = endTime;
        requestLog.duration = duration;
        requestLog.error = errorMessage;

        if (side === 'server') {
            addServerRequest(requestLog);
        } else {
            window.dispatchEvent(
                new CustomEvent('api-request-completed', {
                    detail: requestLog,
                })
            );
        }

        log.error(error);
        if (isVerboseError) {
            throw error;
        }
        throw new Error(`Unknown Error: ${String(error)}`, { cause: error });
    }
}

/**
 * Wraps {@link makeFetch} and throws if the response is an error shape.
 * Pass `responseType: 'void'` for endpoints that return no body (e.g. DELETE).
 */
export const makeFetchOrThrow = async <TResponse, TBody = unknown, TParams = unknown>(
    props: Props<TBody, TParams>
): Promise<TResponse> => {
    const response = await makeFetch<TResponse, TBody, TParams>(props);

    if (isErrorResponse(response)) {
        throw new Error(response.message ?? response.error, { cause: response });
    }

    return response;
};
